import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Envelope, Facebook, Send, Telephone, Whatsapp } from 'react-bootstrap-icons';

const apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
const emptyForm = { name: '', email: '', phone: '', message: '' };
const wakeTimeoutMs = 75000;
const requestTimeoutMs = 75000;
const retryDelayMs = 1500;

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const fetchWithTimeout = async (url, options = {}, timeout = requestTimeoutMs) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const createSubmissionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

function ContactForm() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle');
  const [feedback, setFeedback] = useState('');
  const formRef = useRef(null);
  const warmupPromiseRef = useRef(null);
  const isApiWarmRef = useRef(false);

  const warmApi = useCallback(() => {
    if (isApiWarmRef.current) return Promise.resolve(true);
    if (warmupPromiseRef.current) return warmupPromiseRef.current;

    warmupPromiseRef.current = fetchWithTimeout(
      `${apiBaseUrl}/health`,
      { method: 'GET', cache: 'no-store' },
      wakeTimeoutMs
    )
      .then((response) => {
        if (!response.ok) throw new Error('API health check failed');
        isApiWarmRef.current = true;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        warmupPromiseRef.current = null;
      });

    return warmupPromiseRef.current;
  }, []);

  useEffect(() => {
    const formElement = formRef.current;
    if (!formElement) return undefined;

    if (!('IntersectionObserver' in window)) {
      warmApi();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          warmApi();
          observer.disconnect();
        }
      },
      { rootMargin: '240px' }
    );

    observer.observe(formElement);
    return () => observer.disconnect();
  }, [warmApi]);

  const updateField = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const sendEmail = async (submissionId) => {
    const response = await fetchWithTimeout(`${apiBaseUrl}/send_email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Submission-Id': submissionId
      },
      body: JSON.stringify(form)
    });

    if (!response.ok) {
      const error = new Error('Email request failed');
      error.retryable = response.status >= 500;
      throw error;
    }
  };

  const submitForm = async (event) => {
    event.preventDefault();
    const submissionId = createSubmissionId();
    setStatus('waking');
    setFeedback('מתחברים לשירות המאובטח...');

    try {
      await warmApi();
      setStatus('sending');
      setFeedback('שולחים את הפנייה...');

      try {
        await sendEmail(submissionId);
      } catch (error) {
        const retryable = error.name === 'AbortError' || error instanceof TypeError || error.retryable;
        if (!retryable) throw error;

        isApiWarmRef.current = false;
        setStatus('waking');
        setFeedback('השירות מתעורר, מנסים שוב...');
        await warmApi();
        await wait(retryDelayMs);
        setStatus('sending');
        setFeedback('שולחים את הפנייה...');
        await sendEmail(submissionId);
      }

      setForm(emptyForm);
      setStatus('success');
      setFeedback('הפנייה נשלחה בהצלחה. נחזור אליכם בהקדם.');
    } catch (error) {
      setStatus('error');
      setFeedback('לא הצלחנו לשלוח את הפנייה. אפשר לנסות שוב או ליצור קשר בטלפון.');
    }
  };

  return (
    <div className="content-width contact-layout">
      <div className="contact-copy">
        <p className="eyebrow">צור קשר</p>
        <h2 id="contact-title">מתכננים פרויקט? בואו נדבר.</h2>
        <p>השאירו פרטים ונחזור אליכם כדי להבין את הצורך ולבחון את הדרך המתאימה להתקדם.</p>
        <div className="contact-methods">
          <a href="tel:0545995998"><Telephone aria-hidden="true" /><span><small>טלפון</small>054-599-5998</span></a>
          <a href="mailto:eligman.ltd@gmail.com"><Envelope aria-hidden="true" /><span><small>דוא״ל</small>eligman.ltd@gmail.com</span></a>
          <a href="https://wa.me/message/YNZRORK7GIY2P1" target="_blank" rel="noreferrer"><Whatsapp aria-hidden="true" /><span><small>WhatsApp</small>שליחת הודעה</span></a>
          <a href="https://www.facebook.com/Eligman.Ltd" target="_blank" rel="noreferrer"><Facebook aria-hidden="true" /><span><small>Facebook</small>עמוד החברה</span></a>
        </div>
      </div>

      <form
        ref={formRef}
        className="contact-form"
        onSubmit={submitForm}
        onFocusCapture={warmApi}
      >
        <div className="form-row">
          <label><span>שם מלא</span><input name="name" value={form.name} onChange={updateField} required autoComplete="name" /></label>
          <label><span>טלפון</span><input name="phone" value={form.phone} onChange={updateField} required autoComplete="tel" inputMode="tel" /></label>
        </div>
        <label><span>דוא״ל</span><input name="email" type="email" value={form.email} onChange={updateField} required autoComplete="email" /></label>
        <label><span>כמה מילים על הפרויקט</span><textarea name="message" value={form.message} onChange={updateField} required rows="5" /></label>
        <button
          className="button button-primary submit-button"
          type="submit"
          disabled={status === 'sending' || status === 'waking'}
        >
          <Send aria-hidden="true" />
          {status === 'waking' ? 'מתחברים...' : status === 'sending' ? 'שולחים...' : 'שליחת פנייה'}
        </button>
        <p className={`form-feedback ${status}`} role="status" aria-live="polite">{feedback}</p>
      </form>
    </div>
  );
}

export default ContactForm;
